import type * as React from "react";

import { cn } from "../lib/cn";
import { initials } from "../lib/format";

export type AvatarColor =
  | "primary"
  | "chart-1"
  | "chart-2"
  | "chart-3"
  | "chart-4"
  | "chart-5"
  | "oak"
  | "muted-foreground";

const COLOR: Record<AvatarColor, string> = {
  primary: "bg-primary",
  "chart-1": "bg-chart-1",
  "chart-2": "bg-chart-2",
  "chart-3": "bg-chart-3",
  "chart-4": "bg-chart-4",
  "chart-5": "bg-chart-5",
  oak: "bg-oak",
  "muted-foreground": "bg-muted-foreground",
};

const SIZE = {
  default: "size-8 text-xs",
  sm: "size-6 text-[10px]",
  lg: "size-12 text-[15px]",
  xl: "size-[72px] rounded-xl font-display text-[22px]",
} as const;

/** Stable colour for a name, so the same person keeps the same colour everywhere. */
export function colorForName(name: string): AvatarColor {
  const palette: AvatarColor[] = ["chart-1", "chart-2", "chart-4", "chart-5", "primary"];
  let h = 0;
  for (const ch of name) h = (h * 31 + (ch.codePointAt(0) ?? 0)) >>> 0;
  return palette[h % palette.length] ?? "primary";
}

export interface AvatarProps extends Omit<React.ComponentProps<"span">, "color"> {
  name: string;
  src?: string;
  size?: keyof typeof SIZE;
  /** Companies are square, people are round. */
  square?: boolean;
  color?: AvatarColor;
}

/** A person or company: the photo when there is one, otherwise two initials. */
export function Avatar({ name, src, size = "default", square, color, className, ...props }: AvatarProps) {
  return (
    <span
      data-slot="avatar"
      title={name}
      className={cn(
        "inline-grid shrink-0 place-items-center overflow-hidden rounded-full leading-none font-semibold text-primary-foreground",
        SIZE[size],
        square && size !== "xl" && "rounded-md",
        COLOR[color ?? colorForName(name)],
        className,
      )}
      {...props}
    >
      {src ? (
        <img src={src} alt={name} className="size-full object-cover" />
      ) : (
        <span aria-hidden>{initials(name)}</span>
      )}
      {src ? null : <span className="sr-only">{name}</span>}
    </span>
  );
}
